/**
 * anatomy-lp-checkout.js
 *
 * Landing-page purchase buttons are standard <form is="product-form"> carrying
 * [data-lp-redirect]. The theme's own ProductForm handles the click natively:
 * it does the AJAX add to /cart/add (theme.js ~5591), the exact Ajax Cart API
 * call Shopify's Web Pixel watches, so `product_added_to_cart` fires natively
 * and reaches the official Meta channel (AddToCart) — and on success it
 * dispatches `ajaxProduct:added` (theme.js ~5633). On failure (sold out, etc.)
 * it dispatches `ajaxProduct:error` instead.
 *
 * Checkout behavior is selected by the controlled checkout-path runtime. Drawer
 * mode stops after the native add; direct mode redirects only after the real
 * success event. A failed add never redirects.
 *
 * (Residual race note: `ajaxProduct:added` fires when the cart fetch resolves,
 * which is generally after the pixel has observed the add; for slow-device
 * certainty, back this with a Custom Web Pixel on `product_added_to_cart` and
 * server-side CAPI on the Meta channel.)
 */
(function () {
  'use strict';
  if (window.__ANATOMY_LP_CHECKOUT__) return;
  window.__ANATOMY_LP_CHECKOUT__ = true;

  var pendingRedirect = null;

  // The cart-success event is captured from the /cart/add response one
  // microtask after the fetch resolves, which is also when `ajaxProduct:added`
  // fires. Navigating synchronously from that handler pre-empted the capture
  // and deleted a page-dependent share of add-to-cart events (38-47% on the
  // busiest landing page, 3-9% on the lightest) - enough to reverse the
  // family's add-to-cart ranking against actual orders.
  //
  // The capture now goes out over sendBeacon, which survives unload, so this
  // delay only has to cover the microtask that resolves the enrichment
  // payload. It is a hard ceiling, not a dependency: navigation is scheduled
  // on a plain timer and never waits on an analytics promise resolving. A
  // buyer left sitting on the landing page with a full cart is strictly worse
  // than a lost event.
  var ANALYTICS_FLUSH_MS = 250;

  function checkoutMode(form) {
    try {
      if (window.AnatomyFunnelExperiments && typeof window.AnatomyFunnelExperiments.checkoutModeForForm === 'function') {
        var experimentMode = window.AnatomyFunnelExperiments.checkoutModeForForm(form);
        if (experimentMode === 'direct' || experimentMode === 'drawer') return experimentMode;
      }
    }
    catch (error) {}
    var declared = form.getAttribute('data-an-checkout-default') || '';
    if (declared === 'direct' || declared === 'drawer') return declared;
    return form.hasAttribute('data-lp-redirect') ? 'direct' : 'drawer';
  }

  // Capture phase so the intent is recorded before the product-form handler runs.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (form && typeof form.matches === 'function' && form.matches('form[data-lp-redirect]')) {
      var mode = checkoutMode(form);
      pendingRedirect = mode === 'direct' ? {
        target: form.getAttribute('data-lp-redirect') || '/checkout',
        experimentId: form.getAttribute('data-an-checkout-experiment') || '',
        variant: mode
      } : null;
    }
  }, true);

  // Success: the native AJAX add completed (product_added_to_cart has fired).
  document.addEventListener('ajaxProduct:added', function () {
    if (!pendingRedirect) return;
    var redirect = pendingRedirect;
    pendingRedirect = null;
    window.dispatchEvent(new CustomEvent('anatomy:checkout-redirect-started', {
      detail: redirect
    }));

    var navigated = false;
    var go = function () {
      if (navigated) return;
      navigated = true;
      window.location.assign(redirect.target);
    };
    // Bounded: the buyer reaches checkout on this timer whatever analytics
    // does. A throttled timer (backgrounded tab) only delays a buyer who is
    // not looking at the page; nothing else gates this call.
    window.setTimeout(go, ANALYTICS_FLUSH_MS);
  });

  // Failure (e.g. sold out): stay on the page; do not send an empty cart to checkout.
  document.addEventListener('ajaxProduct:error', function () {
    pendingRedirect = null;
  });
})();
